/**
 * `GlobalDataRepository` — idleblade `UNKO.DataRepository.GlobalDataRepository`의 TS 1:1 포팅.
 *
 * 타입별(9종) 맵에 설정값을 적재하고, 키는 `toLowerCase` 정규화(idleblade 동형). 숫자 파싱은
 * JS `Number`(본질적으로 InvariantCulture — 소수점 `.` 고정), array는 `,` split(문자열은 trim).
 * `getX`(miss시 throw)/`tryGetX`(miss시 undefined)/`setX`/`setValueAutoType`/`resolveFields`/`validate` 제공.
 */
import type { GlobalDataRow, GlobalDataType, IGlobalDataRepository } from "./globalDataTypes.js";
import { parseFlexibleTimeSpan } from "./parseFlexibleTimeSpan.js";
import { checkRange, makeResult, type ValidationIssue, type ValidationResult } from "./validation.js";

/**
 * TSV `type` 컬럼 표기 변형 → 정규형. 대소문자 무시 + `int[]`/`intArray` 양형 허용.
 * 예: `"integer"` → `"int"`, `"int[]"` → `"intArray"`.
 * @throws 알 수 없는 표기가 전달되면 `Error`.
 */
const TYPE_ALIASES: Readonly<Record<string, GlobalDataType>> = {
  int: "int",
  integer: "int",
  float: "float",
  double: "float",
  number: "float",
  string: "string",
  str: "string",
  text: "string",
  bool: "bool",
  boolean: "bool",
  "int[]": "intArray",
  intarray: "intArray",
  "float[]": "floatArray",
  floatarray: "floatArray",
  "string[]": "stringArray",
  stringarray: "stringArray",
  "bool[]": "boolArray",
  boolarray: "boolArray",
  timespan: "timeSpan",
};

export function normalizeGlobalDataType(raw: string): GlobalDataType {
  const t = TYPE_ALIASES[raw.trim().toLowerCase()];
  if (!t) throw new Error(`Unknown global data type: '${raw}'`);
  return t;
}

function finiteNumber(v: string, label: string): number {
  const n = Number(v.trim());
  if (!Number.isFinite(n)) throw new Error(`global '${label}' is not a finite number: '${v}'`);
  return n;
}

function integerNumber(v: string, label: string): number {
  const n = finiteNumber(v, label);
  if (!Number.isInteger(n)) throw new Error(`global '${label}' is not an integer: '${v}'`);
  return n;
}

function boolValue(v: string, label: string): boolean {
  const t = v.trim().toLowerCase();
  if (t === "true") return true;
  if (t === "false") return false;
  throw new Error(`global '${label}' is not a bool: '${v}'`);
}

/** `,` split + 원소 trim. 빈 문자열 → `[]`(idleblade Split 동형, StringArray는 Trim). */
function splitArray(v: string): string[] {
  const t = v.trim();
  if (t === "") return [];
  return t.split(",").map((s) => s.trim());
}

/**
 * 키별 type/범위 검증 스키마 — `GlobalDataRepository.validate(schema)` 인자.
 *
 * `validation.ts`의 `FieldRule`과 **의도적으로 분리된** 별도 타입이다.
 * `GlobalSchemaEntry`는 global.tsv 전용으로 type + range만 선언하고,
 * `FieldRule`은 `createDataValidator`의 임의 시트 cross-ref·enum 규칙에 사용한다.
 * 두 타입은 SSoT 중복이 아니라 책임 범위가 다르다.
 */
export interface GlobalSchemaEntry {
  /** 기대하는 값 타입. 적재된 타입 맵과 불일치하면 오류. */
  readonly type: GlobalDataType;
  /** 미지정=true. `false`로 지정하면 키 누락을 허용한다. */
  readonly required?: boolean;
  /** int/float 스칼라에만 적용되는 하한값. 위반 시 오류. */
  readonly min?: number;
  /** int/float 스칼라에만 적용되는 상한값. 위반 시 오류. */
  readonly max?: number;
}
export type GlobalSchema = Readonly<Record<string, GlobalSchemaEntry>>;

export class GlobalDataRepository implements IGlobalDataRepository {
  private readonly _int = new Map<string, number>();
  private readonly _float = new Map<string, number>();
  private readonly _string = new Map<string, string>();
  private readonly _bool = new Map<string, boolean>();
  private readonly _intArray = new Map<string, number[]>();
  private readonly _floatArray = new Map<string, number[]>();
  private readonly _stringArray = new Map<string, string[]>();
  private readonly _boolArray = new Map<string, boolean[]>();
  private readonly _timeSpan = new Map<string, number>();

  get intDataByKey(): ReadonlyMap<string, number> {
    return this._int;
  }
  get floatDataByKey(): ReadonlyMap<string, number> {
    return this._float;
  }
  get stringDataByKey(): ReadonlyMap<string, string> {
    return this._string;
  }
  get boolDataByKey(): ReadonlyMap<string, boolean> {
    return this._bool;
  }
  get intArrayDataByKey(): ReadonlyMap<string, number[]> {
    return this._intArray;
  }
  get floatArrayDataByKey(): ReadonlyMap<string, number[]> {
    return this._floatArray;
  }
  get stringArrayDataByKey(): ReadonlyMap<string, string[]> {
    return this._stringArray;
  }
  get boolArrayDataByKey(): ReadonlyMap<string, boolean[]> {
    return this._boolArray;
  }
  get timeSpanDataByKey(): ReadonlyMap<string, number> {
    return this._timeSpan;
  }

  /** idleblade `AddData` — Enable 행만, `switch(type)` 파싱 후 toLowerCase 키로 적재. */
  addData(rows: Iterable<GlobalDataRow>): void {
    for (const row of rows) {
      if (row.enable === false || row.enable === "false") continue;
      const key = row.id.toLowerCase();
      switch (normalizeGlobalDataType(row.type)) {
        case "int":
          this._int.set(key, integerNumber(row.value, row.id));
          break;
        case "float":
          this._float.set(key, finiteNumber(row.value, row.id));
          break;
        case "string":
          this._string.set(key, row.value);
          break;
        case "bool":
          this._bool.set(key, boolValue(row.value, row.id));
          break;
        case "intArray":
          this._intArray.set(key, splitArray(row.value).map((s, i) => integerNumber(s, `${row.id}[${i}]`)));
          break;
        case "floatArray":
          this._floatArray.set(key, splitArray(row.value).map((s, i) => finiteNumber(s, `${row.id}[${i}]`)));
          break;
        case "stringArray":
          this._stringArray.set(key, splitArray(row.value));
          break;
        case "boolArray":
          this._boolArray.set(key, splitArray(row.value).map((s, i) => boolValue(s, `${row.id}[${i}]`)));
          break;
        case "timeSpan":
          this._timeSpan.set(key, parseFlexibleTimeSpan(row.value));
          break;
      }
    }
  }

  private _require<T>(map: ReadonlyMap<string, T>, key: string, type: string): T {
    const v = map.get(key.toLowerCase());
    if (v === undefined) throw new Error(`global key not found: ${key} (${type})`);
    return v;
  }

  getInt(key: string): number {
    return this._require(this._int, key, "int");
  }
  getFloat(key: string): number {
    return this._require(this._float, key, "float");
  }
  getString(key: string): string {
    return this._require(this._string, key, "string");
  }
  getBool(key: string): boolean {
    return this._require(this._bool, key, "bool");
  }
  getIntArray(key: string): number[] {
    return this._require(this._intArray, key, "int[]");
  }
  getFloatArray(key: string): number[] {
    return this._require(this._floatArray, key, "float[]");
  }
  getStringArray(key: string): string[] {
    return this._require(this._stringArray, key, "string[]");
  }
  getBoolArray(key: string): boolean[] {
    return this._require(this._boolArray, key, "bool[]");
  }
  getTimeSpan(key: string): number {
    return this._require(this._timeSpan, key, "timeSpan");
  }

  tryGetInt(key: string): number | undefined {
    return this._int.get(key.toLowerCase());
  }
  tryGetFloat(key: string): number | undefined {
    return this._float.get(key.toLowerCase());
  }
  tryGetString(key: string): string | undefined {
    return this._string.get(key.toLowerCase());
  }
  tryGetBool(key: string): boolean | undefined {
    return this._bool.get(key.toLowerCase());
  }
  tryGetIntArray(key: string): number[] | undefined {
    return this._intArray.get(key.toLowerCase());
  }
  tryGetFloatArray(key: string): number[] | undefined {
    return this._floatArray.get(key.toLowerCase());
  }
  tryGetStringArray(key: string): string[] | undefined {
    return this._stringArray.get(key.toLowerCase());
  }
  tryGetBoolArray(key: string): boolean[] | undefined {
    return this._boolArray.get(key.toLowerCase());
  }
  tryGetTimeSpan(key: string): number | undefined {
    return this._timeSpan.get(key.toLowerCase());
  }

  has(key: string): boolean {
    const k = key.toLowerCase();
    return (
      this._int.has(k) ||
      this._float.has(k) ||
      this._string.has(k) ||
      this._bool.has(k) ||
      this._intArray.has(k) ||
      this._floatArray.has(k) ||
      this._stringArray.has(k) ||
      this._boolArray.has(k) ||
      this._timeSpan.has(k)
    );
  }

  resolveFields<T extends Record<string, unknown>>(template: T): T {
    const result: Record<string, unknown> = { ...template };
    for (const field of Object.keys(result)) {
      const key = field.toLowerCase();
      const cur = result[field];
      if (typeof cur === "number") {
        if (this._int.has(key)) result[field] = this._int.get(key);
        else if (this._float.has(key)) result[field] = this._float.get(key);
      } else if (typeof cur === "string") {
        if (this._string.has(key)) result[field] = this._string.get(key);
      } else if (typeof cur === "boolean") {
        if (this._bool.has(key)) result[field] = this._bool.get(key);
      } else if (Array.isArray(cur)) {
        if (this._intArray.has(key)) result[field] = this._intArray.get(key);
        else if (this._floatArray.has(key)) result[field] = this._floatArray.get(key);
        else if (this._stringArray.has(key)) result[field] = this._stringArray.get(key);
        else if (this._boolArray.has(key)) result[field] = this._boolArray.get(key);
      }
    }
    return result as T;
  }

  setInt(key: string, value: number): void {
    this._int.set(key.toLowerCase(), value);
  }
  setFloat(key: string, value: number): void {
    this._float.set(key.toLowerCase(), value);
  }
  setString(key: string, value: string): void {
    this._string.set(key.toLowerCase(), value);
  }
  setBool(key: string, value: boolean): void {
    this._bool.set(key.toLowerCase(), value);
  }
  setIntArray(key: string, value: number[]): void {
    this._intArray.set(key.toLowerCase(), value);
  }
  setFloatArray(key: string, value: number[]): void {
    this._floatArray.set(key.toLowerCase(), value);
  }
  setStringArray(key: string, value: string[]): void {
    this._stringArray.set(key.toLowerCase(), value);
  }
  setBoolArray(key: string, value: boolean[]): void {
    this._boolArray.set(key.toLowerCase(), value);
  }
  setTimeSpan(key: string, value: number): void {
    this._timeSpan.set(key.toLowerCase(), value);
  }

  setValueAutoType(key: string, value: unknown): void {
    if (typeof value === "number") {
      Number.isInteger(value) ? this.setInt(key, value) : this.setFloat(key, value);
    } else if (typeof value === "string") {
      this.setString(key, value);
    } else if (typeof value === "boolean") {
      this.setBool(key, value);
    } else if (Array.isArray(value)) {
      if (value.length === 0) this.setStringArray(key, []);
      else if (typeof value[0] === "number") {
        value.every((v) => Number.isInteger(v))
          ? this.setIntArray(key, value as number[])
          : this.setFloatArray(key, value as number[]);
      } else if (typeof value[0] === "string") this.setStringArray(key, value as string[]);
      else if (typeof value[0] === "boolean") this.setBoolArray(key, value as boolean[]);
      else throw new Error(`setValueAutoType: unsupported array element for '${key}'`);
    } else {
      throw new Error(`setValueAutoType: unsupported type for '${key}'`);
    }
  }

  clearData(): void {
    this._int.clear();
    this._float.clear();
    this._string.clear();
    this._bool.clear();
    this._intArray.clear();
    this._floatArray.clear();
    this._stringArray.clear();
    this._boolArray.clear();
    this._timeSpan.clear();
  }

  private _mapFor(type: GlobalDataType): ReadonlyMap<string, unknown> {
    switch (type) {
      case "int":
        return this._int;
      case "float":
        return this._float;
      case "string":
        return this._string;
      case "bool":
        return this._bool;
      case "intArray":
        return this._intArray;
      case "floatArray":
        return this._floatArray;
      case "stringArray":
        return this._stringArray;
      case "boolArray":
        return this._boolArray;
      case "timeSpan":
        return this._timeSpan;
    }
  }

  /** 스키마(키별 type/required/범위)로 적재 결과를 검증 — 키 누락·타입(맵 분류)·범위. */
  validate(schema: GlobalSchema): ValidationResult {
    const errors: ValidationIssue[] = [];
    for (const [key, entry] of Object.entries(schema)) {
      const k = key.toLowerCase();
      const map = this._mapFor(entry.type);
      if (!map.has(k)) {
        if (entry.required !== false) {
          errors.push({ path: key, rule: "required", message: `'${key}' (${entry.type}) 누락 또는 타입 불일치` });
        }
        continue;
      }
      if ((entry.type === "int" || entry.type === "float") && (entry.min !== undefined || entry.max !== undefined)) {
        const r = checkRange(map.get(k) as number, entry.min, entry.max, key);
        if (r) errors.push(r);
      }
    }
    return makeResult(errors);
  }
}
